import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { AlertCircle, CalendarOff, Loader, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import './DiasCerrados.css';

const toKey = (date) => format(date, 'yyyy-MM-dd');
const keyToDate = (key) => parseISO(key);

const DiasCerrados = () => {
  const { authenticatedFetch } = useAuth();
  const [closedDays, setClosedDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [reason, setReason] = useState('');
  const [showPast, setShowPast] = useState(false);

  const todayKey = toKey(new Date());

  const loadClosedDays = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.CLOSED_DAYS));
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'No se pudieron cargar los días cerrados');
      setClosedDays(data.data || []);
    } catch (e) {
      setError(e.message || 'Error de red');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    loadClosedDays();
  }, [loadClosedDays]);

  const closedByKey = useMemo(() => new Map(closedDays.map((d) => [d.date, d])), [closedDays]);
  const closedDates = useMemo(() => closedDays.map((d) => keyToDate(d.date)), [closedDays]);

  const visibleDays = useMemo(
    () => (showPast ? [...closedDays].reverse() : closedDays.filter((d) => d.date >= todayKey)),
    [closedDays, showPast, todayKey]
  );

  const selectedKey = selectedDate ? toKey(selectedDate) : null;
  const selectedIsClosed = selectedKey ? closedByKey.has(selectedKey) : false;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!selectedKey || selectedIsClosed) return;
    setError(null);
    setNotice(null);
    setSaving(true);
    try {
      const res = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.CLOSED_DAYS), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedKey, reason: reason.trim() || null }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'No se pudo deshabilitar el día');
      setClosedDays((prev) => [...prev, data.data].sort((a, b) => a.date.localeCompare(b.date)));
      const label = format(selectedDate, "EEEE d 'de' MMMM yyyy", { locale: es });
      setNotice(
        data.existingBookings > 0
          ? `Día ${label} deshabilitado. Atención: ya hay ${data.existingBookings} reserva(s) activa(s) que empiezan o terminan ese día; no se modifican automáticamente.`
          : `Día ${label} deshabilitado.`
      );
      setSelectedDate(null);
      setReason('');
    } catch (err) {
      setError(err.message || 'Error de red');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (closedDay) => {
    setError(null);
    setNotice(null);
    try {
      const res = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.CLOSED_DAYS}/${closedDay.id}`), {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'No se pudo habilitar el día');
      setClosedDays((prev) => prev.filter((d) => d.id !== closedDay.id));
      setNotice(`Día ${format(keyToDate(closedDay.date), 'dd/MM/yyyy')} habilitado de nuevo.`);
    } catch (err) {
      setError(err.message || 'Error de red');
    }
  };

  return (
    <div className="dias-cerrados-container">
      <div className="dias-cerrados-header">
        <h1>
          <CalendarOff size={28} />
          Días cerrados
        </h1>
        <p>
          Los días deshabilitados no admiten reservas que empiecen o terminen en ellos, ni desde la web de reservas ni
          desde el portal admin.
        </p>
      </div>

      {error && (
        <div className="dc-alert dc-alert-error">
          <AlertCircle size={18} />
          {error}
        </div>
      )}
      {notice && <div className="dc-alert dc-alert-info">{notice}</div>}

      <div className="dias-cerrados-grid">
        <form className="dc-card" onSubmit={handleAdd}>
          <h2>Deshabilitar un día</h2>
          <DayPicker
            mode="single"
            locale={es}
            numberOfMonths={1}
            selected={selectedDate}
            onSelect={setSelectedDate}
            disabled={{ before: new Date() }}
            modifiers={{ closed: closedDates }}
            modifiersClassNames={{ closed: 'dc-day-closed' }}
          />
          <div className="dc-legend">
            <span className="dc-legend-swatch" /> Día cerrado
          </div>

          <div className="dc-form-group">
            <label htmlFor="dc-reason">Motivo (opcional)</label>
            <input
              id="dc-reason"
              type="text"
              maxLength={200}
              placeholder="Ej.: Navidad"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          {selectedIsClosed && <p className="dc-hint">Este día ya está deshabilitado.</p>}

          <button type="submit" className="dc-btn dc-btn-primary" disabled={!selectedKey || selectedIsClosed || saving}>
            {saving ? <Loader size={16} className="spin" /> : <Plus size={16} />}
            {selectedDate ? `Deshabilitar ${format(selectedDate, 'dd/MM/yyyy')}` : 'Selecciona un día'}
          </button>
        </form>

        <div className="dc-card">
          <div className="dc-list-header">
            <h2>{showPast ? 'Todos los días cerrados' : 'Próximos días cerrados'}</h2>
            <label className="dc-toggle-past">
              <input type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} />
              Mostrar pasados
            </label>
          </div>

          {loading ? (
            <div className="dc-empty">
              <Loader size={20} className="spin" />
            </div>
          ) : visibleDays.length === 0 ? (
            <div className="dc-empty">No hay días cerrados.</div>
          ) : (
            <ul className="dc-list">
              {visibleDays.map((d) => {
                const isPast = d.date < todayKey;
                return (
                  <li key={d.id} className={`dc-list-item ${isPast ? 'past' : ''}`}>
                    <div>
                      <div className="dc-date">
                        {format(keyToDate(d.date), "EEEE d 'de' MMMM yyyy", { locale: es })}
                      </div>
                      {d.reason && <div className="dc-reason">{d.reason}</div>}
                    </div>
                    {!isPast && (
                      <button
                        type="button"
                        className="dc-btn dc-btn-ghost"
                        onClick={() => handleDelete(d)}
                        title="Volver a habilitar este día"
                      >
                        <Trash2 size={16} />
                        Habilitar
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiasCerrados;
